import type { TranslateFn } from "../../i18n";
import type { ViewState, SizeGuideField } from "../types";
import { ArrowLeftIcon } from "../icons";

export function EstimationReviewView({
  productImage, productTitle, estimatedValues, estimationLoading,
  dynamicFields, formRef, formGender,
  sizingUnit, setSizingUnit, formKey, setFormKey,
  updateField, setSizingMethod, setSizingLoading, setView, submitSizing, t,
}: {
  productImage: string;
  productTitle: string;
  estimatedValues: Record<string, number> | null;
  estimationLoading: boolean;
  dynamicFields: SizeGuideField[];
  formRef: React.MutableRefObject<Record<string, string>>;
  formGender: string;
  sizingUnit: string;
  setSizingUnit: (u: string) => void;
  formKey: number;
  setFormKey: React.Dispatch<React.SetStateAction<number>>;
  updateField: (key: string, val: string) => void;
  setSizingMethod: (m: "exact" | "quick" | null) => void;
  setSizingLoading: (v: boolean) => void;
  setView: (v: ViewState) => void;
  submitSizing: (mode: "exact" | "quick") => void;
  t: TranslateFn;
}) {
  const unitLabel = sizingUnit === "in" ? "in" : "cm";

  const SKIP_ESTIMATE_KEYS = new Set(["weight", "weightKg", "height"]);
  const reqFields = dynamicFields.filter(f => f.required && f.unit === "cm" && !SKIP_ESTIMATE_KEYS.has(f.key));

  const handleSubmit = () => {
    formRef.current.gender = formGender;
    setSizingMethod("exact");
    setSizingLoading(true);
    setView("size-result");
    submitSizing("exact");
  };

  // Loading state
  if (estimationLoading || !estimatedValues) {
    return (
      <div className="ps-bp-layout">
        <div className="ps-bp-image">
          <img src={productImage} alt={productTitle} className="ps-bp-image-img" />
        </div>
        <div className="ps-bp-root" style={{ alignItems: "center", justifyContent: "center" }}>
          <div className="ps-tryon-size-loading-spinner" />
          <p style={{ color: "var(--ps-text-muted)", fontSize: "0.8vw", marginTop: "0.6vw" }}>{t("Estimating your measurements...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ps-bp-layout">
      {/* Left — same product image as body profile */}
      <div className="ps-bp-image">
        <img src={productImage} alt={productTitle} className="ps-bp-image-img" />
      </div>

      {/* Right — estimated fields */}
      <div className="ps-bp-root">
        <h3 className="ps-bp-title" style={{ fontSize: "1.1vw" }}>{t("Here's what we estimated")}</h3>
        <p className="ps-bp-subtitle">{t("Tap any value to adjust if needed")}</p>

        <div className="ps-tryon-sf-fields" key={`est-${formKey}`} style={{ flex: 1, overflowY: "auto" }}>
          {reqFields.map(field => {
            const estimated = estimatedValues[field.key];
            const fromForm = formRef.current[field.key] ? parseFloat(formRef.current[field.key]) : null;
            const val = estimated || fromForm;
            if (!val) return null;
            const isUserEntered = !estimated && !!fromForm;
            if (!formRef.current[field.key]) {
              formRef.current[field.key] = String(val);
            }
            return (
              <div key={field.key} className="ps-tryon-sf-float-row">
                <div className="ps-tryon-sf-float-field">
                  <label className="ps-tryon-sf-float-label">
                    {t(field.label)} ({unitLabel}) {isUserEntered
                      ? <span className="ps-tryon-sf-entered-badge">{t("ENTERED")}</span>
                      : <span className="ps-tryon-sf-estimated-badge">{t("ESTIMATED")}</span>}
                  </label>
                  <input
                    type="number"
                    placeholder=" "
                    className="ps-tryon-sf-input"
                    defaultValue={val}
                    onInput={e => updateField(field.key, (e.target as HTMLInputElement).value)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation — same style as body profile */}
        <div className="ps-bp-nav">
          <button type="button" className="ps-bp-back-btn" onClick={() => setView("body-profile")}>
            <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
          </button>
          <button className="ps-bp-next-btn" onClick={handleSubmit}>
            {t("What is my size?")}
          </button>
        </div>
      </div>
    </div>
  );
}
